/**
 * Shared advanced execution options for workflow execution.
 *
 * Used by RunWorkflowDialog (manual execution) and ScheduleFormDialog (scheduled execution).
 * Controls per-user timeout, retry attempts, and concurrency.
 */

import { useState } from 'react';
import type { RuntimeOptions } from '@/types/imageWorkflow';

interface ExecutionAdvancedOptionsProps {
  value: RuntimeOptions;
  onChange: (value: RuntimeOptions) => void;
}

export function ExecutionAdvancedOptions({ value, onChange }: ExecutionAdvancedOptionsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <span>{showAdvanced ? '▾' : '▸'}</span>
        <span>高级选项</span>
      </button>
      {showAdvanced && (
        <div className="mt-3 space-y-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">单用户超时（秒）</label>
            <input
              type="number"
              value={value.per_user_timeout_seconds ?? 300}
              min={60}
              max={900}
              onChange={(e) =>
                onChange({
                  ...value,
                  per_user_timeout_seconds: Math.max(60, Math.min(900, parseInt(e.target.value) || 300)),
                })
              }
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">每个用户最大执行时间，60-900秒</p>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">重试次数</label>
            <input
              type="number"
              value={value.max_attempts ?? 2}
              min={1}
              max={5}
              onChange={(e) =>
                onChange({
                  ...value,
                  max_attempts: Math.max(1, Math.min(5, parseInt(e.target.value) || 2)),
                })
              }
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">每个用户的总执行次数（含首次），1-5</p>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">并行度</label>
            <input
              type="number"
              value={value.max_concurrency ?? 10}
              min={1}
              max={50}
              onChange={(e) =>
                onChange({
                  ...value,
                  max_concurrency: Math.max(1, Math.min(50, parseInt(e.target.value) || 10)),
                })
              }
              className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">同时执行的最大用户数，1-50</p>
          </div>
        </div>
      )}
    </div>
  );
}
